'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from '../../../lib/utils'
import { Button, ButtonProps } from '../Button'
import { FormComponent, FormLayout } from '../FormLayout'
import Icon from '../Icon'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay title="dialog-overlay" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-0 border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <Icon name="X" size={14} title="dialog-close" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left pb-4',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  DialogView,
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}

export type DialogProps = React.ComponentProps<typeof Dialog> & {
  open: boolean
  title: string
  description?: string
  actions: ButtonProps[]
  isLoading?: boolean
  sections: {
    hidden?: boolean
    noPadding?: boolean
    label?: string
    actions?: ButtonProps[]
    fields: FormComponent[]
  }[]
  handleClose?: () => void
}

const DialogView = React.forwardRef((props: DialogProps, ref) => {
  const {
    title,
    description,
    actions,
    sections,
    open,
    handleClose,
    isLoading,
  } = props

  const onOpenChange = (open: boolean) => {
    !open && handleClose && handleClose()
  }

  const activeSections = sections.filter((section) => !section.hidden)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[70vh] overflow-hidden flex flex-col"
        id="dialog"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-4 px-4 py-1">
          {activeSections.map((section, i) => (
            <div key={i}>
              {section.label && (
                <div className="text-xs uppercase font-bold">
                  {section.label}
                </div>
              )}
              {section.actions && (
                <div className="flex gap-2 flex-wrap w-full -ml-4">
                  {section.actions?.map((action, i) => (
                    <Button
                      isSkeleton={isLoading}
                      key={i}
                      {...action}
                      variant="link"
                    />
                  ))}
                </div>
              )}

              <div className={cn({ '-mx-6': section.noPadding })}>
                <FormLayout fields={section.fields} />

                {i < activeSections.length - 1 && (
                  <div className="-mx-6 my-4">
                    <hr />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex-shrink-0">
          {actions.length > 0 && (
            <div className="pt-6 w-full flex flex-row-reverse gap-2">
              {actions.map((action, i) => (
                <Button key={i} {...action} />
              ))}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})

DialogView.displayName = 'DialogView'
